import { SPACING } from '@alto/design-library-tokens';
import {
  Body,
  Button,
  ButtonGroup,
  Column,
  Description,
  InlineAlert,
  LgPadding,
  Row,
  SmSpacing,
} from '@alto/design-system';
import { InsuranceFormFields, useInsuranceWarningActionSheet } from '@alto/features';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { DEFAULT_ERROR_ALERT_TITLE } from '~shared/constants';
import {
  type Values,
  getInitialInsuranceFormValues,
} from '~shared/features/insurances/helpers/getInitialInsuranceFormValues';
import { usePageViewedEvent } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { type Insurance } from '~shared/types/clients';

type Props = {
  readonly onSubmit: (insurance: Values) => Promise<void>;
  readonly onCancel: () => void;
  readonly insuranceError: Error | null | undefined;
  readonly selectedInsurance: Insurance | null | undefined;
  readonly onShowPhotoForm?: () => void;
  readonly loading: boolean;
};

export const InsuranceForm = ({
  insuranceError,
  onCancel,
  onSubmit,
  selectedInsurance,
  onShowPhotoForm,
  loading,
}: Props) => {
  const methods = useForm({
    defaultValues: getInitialInsuranceFormValues(selectedInsurance),
    mode: 'onBlur',
  });

  const submitText = selectedInsurance ? 'Update' : 'Add';
  const { showActionSheet } = useInsuranceWarningActionSheet({ handleContinue: onCancel });

  usePageViewedEvent({
    event: EVENTS.ADD_INSURANCE_VIEWED,
  });

  return (
    <LgPadding
      topPadding={SPACING.STATIC.MD}
      bottomPadding={SPACING.STATIC.NONE}
    >
      <Column gap="MD">
        <InlineAlert>
          <Description fontFamily="semibold">Where can I find this?</Description>
          <Description>
            Typically, your prescription benefit card will have an &quot;Rx&quot; symbol and &quot;BIN&quot; and
            &quot;PCN&quot; numbers.
          </Description>
        </InlineAlert>
        <FormProvider {...methods}>
          <InsuranceFormFields />
        </FormProvider>
        {!selectedInsurance && onShowPhotoForm ? (
          <>
            <InlineAlert>
              <Row
                spaceBetween
                centerVertically
              >
                <Body>Or upload photos of your benefit card</Body>
                <SmSpacing />
                <Button
                  label="Upload photos"
                  type="secondary"
                  onPress={onShowPhotoForm}
                  small
                />
              </Row>
            </InlineAlert>
          </>
        ) : null}
        {insuranceError && !showActionSheet ? (
          <>
            <InlineAlert type="error">
              <Body fontFamily="semibold">{DEFAULT_ERROR_ALERT_TITLE}</Body>
              <Body>There was an error submitting your benefit information. Please try again.</Body>
            </InlineAlert>
          </>
        ) : null}
        <ButtonGroup
          justifyButtons="right"
          inline
          buttons={[
            <Button
              key="cancel-button"
              label="Cancel"
              type="tertiary"
              onPress={onCancel}
            />,
            <Button
              key="save-button"
              label={`${submitText} benefits`}
              loading={loading}
              loadingLabel="Saving benefits"
              onPress={methods.handleSubmit(onSubmit)}
            />,
          ]}
        />
      </Column>
    </LgPadding>
  );
};
